import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CreateEstimationItemPayload,
  CreateEstimationPayload,
  EstimationDetail,
  EstimationItem,
  EstimationListItem,
  EstimationStatus,
  PaginatedResponse,
  UpdateEstimationItemPayload,
  UpdateEstimationPayload,
} from "../types";

const ESTIMATIONS_KEY = ["estimations"] as const;

export type EstimationListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
};

export function useEstimationList(
  params: EstimationListParams = {},
): UseQueryResult<PaginatedResponse<EstimationListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...ESTIMATIONS_KEY,
      "list",
      params,
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<EstimationListItem>>("/estimations", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
        },
      }),
  });
}

export function useEstimation(id: string): UseQueryResult<EstimationDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...ESTIMATIONS_KEY,
      "detail",
      id,
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ estimation: EstimationDetail }>(
        `/estimations/${id}`,
        { token: accessToken },
      );
      return res.estimation;
    },
  });
}

export function useCreateEstimation() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEstimationPayload) =>
      apiFetch<{ estimation: EstimationDetail }>("/estimations", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ESTIMATIONS_KEY] });
    },
  });
}

export function useUpdateEstimation(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateEstimationPayload) =>
      apiFetch<{ estimation: EstimationDetail }>(`/estimations/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ESTIMATIONS_KEY] });
    },
  });
}

export function useUpdateEstimationStatus() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: EstimationStatus }) =>
      apiFetch<{ estimation: EstimationDetail }>(`/estimations/${id}/status`, {
        method: "PUT",
        body: { status },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ESTIMATIONS_KEY] });
    },
  });
}

export function useDeleteEstimation() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/estimations/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ESTIMATIONS_KEY] });
    },
  });
}

export function useDuplicateEstimation() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ estimation: EstimationDetail }>(
        `/estimations/${id}/duplicate`,
        { method: "POST", token: accessToken },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ESTIMATIONS_KEY] });
    },
  });
}

export function useAddEstimationItem(estimationId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEstimationItemPayload) =>
      apiFetch<{ item: EstimationItem; estimation: EstimationListItem }>(
        `/estimations/${estimationId}/items`,
        { method: "POST", body: payload, token: accessToken },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ESTIMATIONS_KEY] });
    },
  });
}

export function useUpdateEstimationItem(estimationId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      payload,
    }: {
      itemId: string;
      payload: UpdateEstimationItemPayload;
    }) =>
      apiFetch<{ item: EstimationItem; estimation: EstimationListItem }>(
        `/estimations/${estimationId}/items/${itemId}`,
        { method: "PUT", body: payload, token: accessToken },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ESTIMATIONS_KEY] });
    },
  });
}

export function useDeleteEstimationItem(estimationId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      apiFetch<{ estimation: EstimationListItem }>(
        `/estimations/${estimationId}/items/${itemId}`,
        { method: "DELETE", token: accessToken },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...ESTIMATIONS_KEY] });
    },
  });
}
